// Must stay the first import: it has to evaluate before every other module.
// See bootRedirect.ts for why inline code here would run too late.
import './bootRedirect'
// Next: the native sign-in handoff leaves the address bar before anything records it.
import './handoff'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import * as Sentry from "@sentry/react";
import { AnalyticsProvider } from './analytics.tsx'
import { PRELOAD_RELOAD_KEY, preloadReloadAllowed, updateReloader } from './updateReload'

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", "https://double-bagel-9f766.web.app", "https://double-bagel-9f766.firebaseapp.com", "https://double-bagel-staging.web.app", "http://localhost:5173", "https://doublebagelapp.com"],
    beforeSend(event) {
      // Service worker registration can fail in incognito mode, WebViews,
      // or when the user navigates away before the SW installs — non-fatal.
      const isSwError = event.exception?.values?.some((ex) =>
        ex.stacktrace?.frames?.some((f) => f.filename?.includes('registerSW'))
      );
      if (isSwError) return null;
      return event;
    },
  });
}

// Build id injected by versionJsonPlugin in vite.config.ts — the same value
// useVersionCheck compares against /version.json. Declared locally because the
// project has no shared ambient .d.ts; useVersionCheck.ts declares it the same
// way.
declare const __APP_VERSION__: string;

// After a deploy, lazy-loaded chunks from the old bundle 404. Vite fires this
// event when that happens — reloading gets users onto the new version. The guard
// holds the build this tab was running when it reloaded (updateReload.ts): a
// reload that lands back on that same bundle (the worker still serving it) is
// refused, which breaks the loop, and the next deploy's missing file reloads
// again. It held a bare '1' for the tab's life, so only the first deploy ever
// recovered (4 Nov).
window.addEventListener('vite:preloadError', () => {
  if (!preloadReloadAllowed(sessionStorage.getItem(PRELOAD_RELOAD_KEY), __APP_VERSION__)) return;
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, __APP_VERSION__);
  window.location.reload();
});

// ── Deliberate reload when a new service worker takes control ────────────────
// The worker is built with skipWaiting + clientsClaim (vite.config.ts), so a new
// worker activates and claims this page as soon as it installs. Claiming changes
// which assets are SERVED; it does not change the JavaScript already RUNNING.
// Without this listener the page limps along on the old bundle until it happens
// to request a lazy chunk that is gone and falls into vite:preloadError above.
//
// The key stores the build id this page was RUNNING when it last reloaded for a
// worker takeover, so a reload that lands back on the same bundle is refused
// while every deploy still gets one.
const SW_RELOAD_KEY = 'sw-reload';

const alreadyReloadedThisBuild = () =>
  sessionStorage.getItem(SW_RELOAD_KEY) === __APP_VERSION__;

// Read at module evaluation, before any worker can claim this page. A null
// controller means nothing was controlling us at load — a first install rather
// than an update, where reloading would only replay the bundle we already have.
const hadControllerAtLoad =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  navigator.serviceWorker.controller !== null;

// Never mid-work (updateReload.ts): a page on a call, uploading or editing keeps
// running; the reload waits for the next navigation that lands somewhere free,
// and "Update available" says one is waiting. React Router moves with
// pushState/replaceState and Back with popstate, so those are the navigations.
const reloader = updateReloader();
for (const method of ['pushState', 'replaceState'] as const) {
  const original = history[method].bind(history);
  history[method] = (...args: Parameters<History['pushState']>) => {
    original(...args);
    reloader.navigated();
  };
}
window.addEventListener('popstate', () => reloader.navigated());

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerAtLoad || alreadyReloadedThisBuild()) return;
    reloader.request(() => {
      if (alreadyReloadedThisBuild()) return;
      sessionStorage.setItem(SW_RELOAD_KEY, __APP_VERSION__);
      window.location.reload();
    });
  });
}

// In dev, defensively unregister any service worker left over from a prior prod
// build. A stale SW caching old bundles is a common cause of "blank page on hard
// refresh" and "0 results on first nav" symptoms in dev.
if (import.meta.env.DEV && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  }).catch(() => { /* non-fatal */ });
}

createRoot(document.getElementById('root')!).render(
  <AnalyticsProvider>
    <App />
  </AnalyticsProvider>,
)
